/*
  Header search bar (snippets/bsr-search.liquid, assets/bsr-search.css).

  Layout: on desktop the bar sits in the header row when an input of at least
  data-min-width (350px) fits between the logo and the menu without moving the
  menu (Figma note). The fit is measured, not tied to a breakpoint, so a
  shorter menu gets the inline bar on smaller screens. Otherwise, and on
  tablet / phone, the header search icons open the bar under the header.

  The layout is decided on the full header at the top of the page. The sticky
  header (body.has-scrolled) shrinks the logo by 50px, which could let the bar
  fit inline only while scrolled and flip it on every scroll; while scrolled the
  last full-header decision is reused, so the bar stays in one spot.

  Search page (/search): the theme's own page search bar is hidden
  (bsr-search.css). With the bar inline nothing replaces it; in dropdown mode
  (smaller desktop, tablet, phone) the bar is "pinned" open under the sticky
  header in that same slot, holding the query, so it stays on screen while the
  results scroll. A pinned bar only closes from its X; outside clicks, Escape
  and menu hovers close the results, not the bar.

  Search: same query as the theme's search page and its "View All Results"
  link (type=product, prefix on the last word). One request per query through
  the Section Rendering API (sections/bsr-search-results.liquid), cached per
  page view; stale responses are aborted.
*/
(() => {
  if (customElements.get('bsr-search')) return;

  const DESKTOP_QUERY = '(min-width: 1024px)';
  const NO_PAGE_BAR_CLASS = 'bsr-search-no-page-bar';
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;
  const CACHE_SIZE = 30;
  const MENU_HANDOFF_MS = 150;

  // Spacing from the Figma header: logo -> icon 60, icon -> field 16,
  // field -> first menu item 60.
  const GAP_LOGO = 60;
  const GAP_ICON = 16;
  const GAP_MENU = 60;

  class BsrSearch extends HTMLElement {
    connectedCallback() {
      // The theme measures the header on a deep clone (getMinWidth); the clone
      // must stay inert or it would bind listeners and re-measure forever.
      if (this.closest('.js__header__clone')) return;
      if (this.initialized) return;
      this.initialized = true;

      this.header = this.closest('header-component') || this.closest('[data-site-header]');
      this.wrapper = this.closest('[data-wrapper]') || this.parentElement;
      this.form = this.querySelector('[data-bsr-search-form]');
      this.input = this.querySelector('[data-bsr-search-input]');
      this.panel = this.querySelector('[data-bsr-search-panel]');
      this.body = this.querySelector('[data-bsr-search-body]');
      this.live = this.querySelector('[data-bsr-search-live]');
      this.icon = this.querySelector('.bsr-search__icon');

      this.searchUrl = this.dataset.searchUrl || '/search';
      this.sectionId = this.dataset.sectionId;
      this.minWidth = Number(this.dataset.minWidth) || 350;
      this.maxWidth = Number(this.dataset.maxWidth) || 500;

      this.cache = new Map();
      this.controller = null;
      this.requestId = 0;
      this.renderedTerms = null;
      this.debounceTimer = 0;
      this.layoutFrame = 0;
      this.lastTrigger = null;
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.topLayout = null;
      this.searchPage = this.hasAttribute('data-search-page');
      this.pinned = false;
      this.pinDismissed = false;
      // The search page prefills the field with the query its results already show.
      this.pageTermsKey = this.searchPage ? this.cacheKey(this.terms()) : null;
      // Every listener outside this element goes through one signal, so a
      // re-rendered header (theme editor) leaves nothing behind.
      this.listeners = new AbortController();

      this.bindEvents();
      this.observeLayout();
      this.updateLayout();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.initialized = false;
      this.abort();
      clearTimeout(this.debounceTimer);
      clearTimeout(this.menuTimer);
      cancelAnimationFrame(this.layoutFrame);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.listeners.abort();
      if (this.searchPage) document.body.classList.remove(NO_PAGE_BAR_CLASS);
    }

    /* ---------- Events ---------- */

    bindEvents() {
      const {signal} = this.listeners;
      this.scheduleLayout = this.scheduleLayout.bind(this);

      // Capture phase, so the header icons never fall through to the theme's
      // own search handlers or to the /search link itself.
      document.addEventListener('click', (event) => this.onTriggerClick(event), {capture: true, signal});
      document.addEventListener('pointerdown', (event) => this.onOutsidePointer(event), {capture: true, signal});
      document.addEventListener('keydown', (event) => this.onKeydown(event), {signal});

      this.input.addEventListener('input', () => this.onInput(), {signal});
      this.input.addEventListener(
        'focus',
        () => {
          if (!this.quietFocus) this.showCachedResults();
        },
        {signal}
      );
      this.input.addEventListener(
        'click',
        () => {
          if (!this.classList.contains('has-results')) this.showCachedResults();
        },
        {signal}
      );

      this.form.addEventListener(
        'submit',
        (event) => {
          if (!this.terms()) {
            event.preventDefault();
            this.input.focus();
          }
        },
        {signal}
      );

      this.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('[data-bsr-search-close]')) return;
          event.preventDefault();
          // The X of a pinned bar puts the bar away for the rest of the page view;
          // focus goes to the header search icon that can bring it back.
          if (this.pinned) {
            this.pinDismissed = true;
            this.setPinned(false);
            this.close();
            this.syncPin();
            const icon = Array.from(this.header?.querySelectorAll('[data-nav-search-open]') || []).find(
              (trigger) => trigger.offsetParent !== null
            );
            icon?.focus({preventScroll: true});
            return;
          }
          this.close({restoreFocus: true});
        },
        {signal}
      );

      // Keyboard users tabbing out of the bar or the results close them.
      this.addEventListener(
        'focusout',
        (event) => {
          const next = event.relatedTarget;
          if (next && !this.contains(next) && !this.isTrigger(next)) this.close();
        },
        {signal}
      );

      // The header menus open their own panels in the same spot, so resting on
      // a menu item hands the space over to it. The short delay lets a pointer
      // merely crossing the menu (icon -> bar) keep the search open.
      const nav = this.header?.querySelector('[data-nav]');
      nav?.addEventListener(
        'pointerover',
        (event) => {
          clearTimeout(this.menuTimer);
          if (!this.classList.contains('is-open') && !this.classList.contains('has-results')) return;
          const onMenuItem = this.menuElements().some((menu) =>
            Array.from(menu.children).some((item) => item.contains(event.target))
          );
          if (onMenuItem) this.menuTimer = setTimeout(() => this.close(), MENU_HANDOFF_MS);
        },
        {signal}
      );
      nav?.addEventListener('pointerleave', () => clearTimeout(this.menuTimer), {signal});

      // The panel's size depends on where it starts and on the viewport height.
      const onViewportChange = () => {
        if (this.classList.contains('has-results')) this.updatePanelTop();
      };
      window.addEventListener('scroll', onViewportChange, {passive: true, signal});
      window.addEventListener('resize', onViewportChange, {passive: true, signal});
    }

    isTrigger(element) {
      return Boolean(element?.closest?.('[data-nav-search-open]'));
    }

    onTriggerClick(event) {
      // Header icons, plus the "Search" links in the mobile menu drawers.
      const trigger = event.target.closest?.('[data-nav-search-open]');
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.mode === 'inline' || this.pinned) {
        this.input.focus();
        return;
      }

      if (this.classList.contains('is-open')) {
        this.close({restoreFocus: true});
      } else {
        this.lastTrigger = trigger;
        this.open();
      }
    }

    onOutsidePointer(event) {
      const active = this.classList.contains('has-results') || (this.classList.contains('is-open') && !this.pinned);
      if (!active) return;
      if (this.contains(event.target) || this.isTrigger(event.target)) return;
      this.close();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      // A pinned bar with no results open leaves Escape to the theme (drawers).
      const active = this.classList.contains('has-results') || (this.classList.contains('is-open') && !this.pinned);
      if (!active) return;
      event.preventDefault();
      this.close({restoreFocus: true});
    }

    onInput() {
      clearTimeout(this.debounceTimer);
      const terms = this.terms();

      if (terms.length < MIN_CHARS) {
        this.abort();
        this.hideResults();
        return;
      }

      // A cached query renders at once; only network requests wait for typing to settle.
      if (this.cache.has(this.cacheKey(terms))) {
        this.search(terms);
        return;
      }

      this.debounceTimer = setTimeout(() => this.search(terms), DEBOUNCE_MS);
    }

    /* ---------- Open / close ---------- */

    open({select = true} = {}) {
      // Leave other header drawers before dropping the bar.
      document.querySelectorAll('drawer-element.is-open').forEach((drawer) => drawer.close?.());

      this.classList.add('is-open');
      this.panel.scrollTop = 0;
      this.setTriggersExpanded(true);
      // Focus synchronously, inside the click, so iOS raises the keyboard.
      // The focus listener then brings back results for a kept query.
      this.input.focus({preventScroll: true});
      // A kept query is selected, so typing replaces it.
      if (select) this.input.select();
      if (document.activeElement !== this.input) this.showCachedResults();
    }

    close({restoreFocus = false} = {}) {
      clearTimeout(this.debounceTimer);
      this.abort();

      // A pinned bar stays; only its results close.
      if (this.pinned) {
        this.hideResults();
        if (restoreFocus && this.contains(document.activeElement)) this.focusInputQuietly();
        return;
      }

      const wasOpen = this.classList.contains('is-open');
      this.hideResults();
      this.classList.remove('is-open');
      this.setTriggersExpanded(false);

      if (!restoreFocus) return;
      if (wasOpen && this.lastTrigger?.isConnected && this.lastTrigger.offsetParent !== null) {
        this.lastTrigger.focus({preventScroll: true});
      } else if (this.mode === 'inline' && this.contains(document.activeElement)) {
        this.focusInputQuietly();
      }
    }

    // Focus the field without its focus listener reopening the results.
    focusInputQuietly() {
      this.quietFocus = true;
      this.input.focus({preventScroll: true});
      this.quietFocus = false;
    }

    setTriggersExpanded(expanded) {
      this.header?.querySelectorAll('[data-nav-search-open]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(expanded));
      });
    }

    showResults() {
      if (this.live && this.live.textContent !== this.statusText) this.live.textContent = this.statusText || '';
      this.classList.add('has-results');
      this.input.setAttribute('aria-expanded', 'true');
      this.updatePanelTop();
    }

    hideResults() {
      if (this.live) this.live.textContent = '';
      this.classList.remove('has-results', 'is-loading');
      this.input.setAttribute('aria-expanded', 'false');
    }

    // Reopen the last results when the field gets focus again (inline mode).
    showCachedResults() {
      const terms = this.terms();
      if (terms.length < MIN_CHARS) return;
      const key = this.cacheKey(terms);
      // Focusing the prefilled field on the search page shouldn't drop a copy of
      // the results the page already lists; editing the query does.
      if (key === this.pageTermsKey && this.renderedTerms !== key) return;
      if (this.renderedTerms === key) {
        this.showResults();
      } else if (this.pendingKey !== key) {
        this.search(terms);
      }
    }

    // The panel scrolls inside the viewport; its top moves with the sticky header.
    updatePanelTop() {
      const top = `${Math.max(0, Math.round(this.panel.getBoundingClientRect().top))}px`;
      if (top !== this.panelTop) {
        this.panelTop = top;
        this.style.setProperty('--bsr-panel-top', top);
      }

      // Shopify's preview bar (theme previews only) covers the bottom of the
      // viewport; keep the panel above it so a preview shows what live will.
      // "Hide bar" removes it; a collapsed one has no height and covers nothing.
      const previewRect = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect();
      const barTop = previewRect && previewRect.height > 0 ? previewRect.top : window.innerHeight;
      const bottom = `${Math.max(0, Math.round(window.innerHeight - Math.min(window.innerHeight, barTop)))}px`;
      if (bottom !== this.panelBottom) {
        this.panelBottom = bottom;
        this.style.setProperty('--bsr-panel-bottom', bottom);
      }

      // Lets the pinned "View All Results" row show a shadow over scrolled content.
      this.panel.classList.toggle('is-overflowing', this.panel.scrollHeight > this.panel.clientHeight + 1);
    }

    /* ---------- Search ---------- */

    terms() {
      return this.input.value.trim();
    }

    cacheKey(terms) {
      return terms.replace(/\s+/g, ' ').toLowerCase();
    }

    abort() {
      this.controller?.abort();
      this.controller = null;
      this.pendingKey = null;
      this.classList.remove('is-loading');
    }

    async search(terms) {
      const key = this.cacheKey(terms);
      const requestId = ++this.requestId;

      if (this.cache.has(key)) {
        this.abort();
        this.render(key, this.cache.get(key));
        return;
      }

      this.abort();
      this.controller = new AbortController();
      this.pendingKey = key;
      this.classList.add('is-loading');

      const params = new URLSearchParams({
        q: terms,
        type: 'product',
        'options[prefix]': 'last',
        section_id: this.sectionId,
      });

      try {
        const response = await fetch(`${this.searchUrl}?${params.toString()}`, {
          signal: this.controller.signal,
          headers: {Accept: 'text/html'},
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const html = await response.text();
        if (requestId !== this.requestId) return;

        const results = this.parse(html);
        if (!results) throw new Error('Search results section missing');

        this.remember(key, results);
        this.render(key, results);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestId === this.requestId) this.hideResults();
        console.warn('[bsr-search]', error);
      } finally {
        if (requestId === this.requestId) {
          this.controller = null;
          this.pendingKey = null;
          this.classList.remove('is-loading');
        }
      }
    }

    parse(html) {
      const template = document.createElement('template');
      template.innerHTML = html;
      return template.content.querySelector('[data-bsr-search-results]');
    }

    remember(key, results) {
      if (this.cache.size >= CACHE_SIZE) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, results);
    }

    render(key, results) {
      // The field changed while this was loading.
      if (this.cacheKey(this.terms()) !== key) return;

      // Keep the cached node pristine so it can be rendered again.
      const node = results.cloneNode(true);
      const status = node.querySelector('[data-bsr-search-status]');
      if (status?.classList.contains('visually-hidden')) status.remove();

      this.body.replaceChildren(node);
      this.renderedTerms = key;
      this.statusText = status ? status.textContent.trim() : '';
      this.panel.scrollTop = 0;

      if (this.isOpenForResults()) this.showResults();
    }

    // Results only show while the bar is usable: open (dropdown) or focused (inline).
    isOpenForResults() {
      if (this.mode === 'inline' || this.pinned) return this.contains(document.activeElement);
      return this.classList.contains('is-open');
    }

    /* ---------- Layout ---------- */

    observeLayout() {
      const {signal} = this.listeners;
      this.desktopQuery.addEventListener('change', this.scheduleLayout, {signal});

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.scheduleLayout);
        this.resizeObserver.observe(this.wrapper);
        this.menuElements().forEach((element) => this.resizeObserver.observe(element));
      } else {
        window.addEventListener('resize', this.scheduleLayout, {signal});
      }

      // Header compress toggles a class; app menus (Globo) render late.
      this.mutationObserver = new MutationObserver(this.scheduleLayout);
      if (this.header) this.mutationObserver.observe(this.header, {attributes: true, attributeFilter: ['class']});
      this.menuElements().forEach((element) => this.mutationObserver.observe(element, {childList: true}));

      document.fonts?.ready.then(this.scheduleLayout);
      window.addEventListener('load', this.scheduleLayout, {once: true, signal});
    }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.updateLayout());
    }

    // The header's menu containers: everything in the nav except its icon groups.
    menuElements() {
      const nav = this.header?.querySelector('[data-nav]');
      if (!nav) return [];
      return Array.from(nav.children).filter(
        (child) => !child.matches('[data-nav-icons], [data-nav-items-compress], .menu__item--compress')
      );
    }

    // Left edge of the first visible top-level menu item, or null if no menu is
    // rendered yet (an app menu still loading).
    menuLeft() {
      let left = Infinity;
      this.menuElements().forEach((element) => {
        if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') return;
        Array.from(element.children).forEach((child) => {
          const rect = child.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) left = Math.min(left, rect.left);
        });
      });
      return Number.isFinite(left) ? left : null;
    }

    logoRight() {
      const logo = this.header?.querySelector('[data-logo]');
      if (!logo) return null;
      let right = 0;
      logo.querySelectorAll('a, img').forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width > 0) right = Math.max(right, rect.right);
      });
      return right || logo.getBoundingClientRect().right;
    }

    // Width the header search icon frees when it hides in inline mode; the
    // right-aligned menu moves right by that much.
    freedIconWidth() {
      if (this.header?.dataset.navAlignment !== 'right') return 0;
      if (this.mode === 'inline') return 0;
      const trigger = this.header.querySelector('[data-nav-icons] [data-nav-search-open]');
      const item = trigger?.closest('.menu__item--icon') || trigger;
      return item ? item.getBoundingClientRect().width : 0;
    }

    measureInline() {
      if (!this.desktopQuery.matches) return null;
      if (this.header?.classList.contains('site-header--compress')) return null;

      // Sticky header: keep the full header's decision for this width. With none
      // yet (page opened scrolled), stay in dropdown until the top is reached.
      if (document.body.classList.contains('has-scrolled')) {
        return this.topLayout?.viewport === window.innerWidth ? this.topLayout.inline : null;
      }

      const inline = this.measureFullHeader();
      this.topLayout = {viewport: window.innerWidth, inline};
      return inline;
    }

    measureFullHeader() {
      const logoRight = this.logoRight();
      const menuLeft = this.menuLeft();
      if (logoRight === null || menuLeft === null) return null;

      const wrapperLeft = this.getBoundingClientRect().left;
      const iconWidth = this.icon ? this.icon.getBoundingClientRect().width || 22 : 0;
      const formLeft = logoRight + GAP_LOGO;
      const fieldLeft = formLeft + iconWidth + GAP_ICON;
      const available = menuLeft + this.freedIconWidth() - GAP_MENU - fieldLeft;

      if (available < this.minWidth) return null;

      return {
        left: Math.round(formLeft - wrapperLeft),
        width: Math.floor(Math.min(available, this.maxWidth)),
      };
    }

    updateLayout() {
      if (!this.initialized) return;
      const inline = this.measureInline();
      const mode = inline ? 'inline' : 'dropdown';

      if (inline && (inline.left !== this.inlineLeft || inline.width !== this.inlineWidth)) {
        this.inlineLeft = inline.left;
        this.inlineWidth = inline.width;
        this.style.setProperty('--bsr-left', `${inline.left}px`);
        this.style.setProperty('--bsr-input-width', `${inline.width}px`);
      }

      if (mode === this.mode) {
        this.syncPin();
        return;
      }

      const hadFocus = this.contains(document.activeElement);
      this.close();
      this.mode = mode;
      this.dataset.mode = mode;
      this.header?.classList.toggle('site-header--bsr-search-inline', mode === 'inline');
      this.syncPin();
      // The layout changed under someone typing (window resize): carry on.
      if (!hadFocus) return;
      if (mode === 'inline' || this.pinned) {
        this.focusInputQuietly();
        this.showCachedResults();
      } else {
        this.open({select: false});
      }
    }

    /* ---------- Search page ---------- */

    // Pinned: search page, dropdown layout, not put away with its X.
    syncPin() {
      if (!this.searchPage) return;
      const pin = this.mode === 'dropdown' && !this.pinDismissed;
      if (pin !== this.pinned) this.setPinned(pin);
      // Without a pinned bar the slot it sits in collapses.
      document.body.classList.toggle(NO_PAGE_BAR_CLASS, !this.pinned);
    }

    setPinned(pin) {
      this.pinned = pin;
      this.classList.toggle('is-pinned', pin);
      this.classList.toggle('is-open', pin);
      if (!pin) this.hideResults();
      this.setTriggersExpanded(pin);
    }
  }

  customElements.define('bsr-search', BsrSearch);
})();
